<script setup lang="ts">
import { ref, watch } from "vue";
import { svgIcon } from "@/common/components";

const props = defineProps<{
  zoom: number;
}>();

const emit = defineEmits(["update:zoom", "close", "download"]);

const zoomInputValue = ref((props.zoom * 100).toFixed(0));
let intervalId: number | null = null;

// Sincronizar input com prop zoom
watch(
  () => props.zoom,
  (newZoom) => {
    zoomInputValue.value = (newZoom * 100).toFixed(0);
  },
);

const handleZoomDecrease = () => {
  const newZoom = Math.max(props.zoom - 0.01, 0.1);
  emit("update:zoom", newZoom);
};

const handleZoomIncrease = () => {
  const newZoom = Math.min(props.zoom + 0.01, 2);
  emit("update:zoom", newZoom);
};

const startZoomDecrease = () => {
  handleZoomDecrease();
  intervalId = window.setInterval(handleZoomDecrease, 50);
};

const startZoomIncrease = () => {
  handleZoomIncrease();
  intervalId = window.setInterval(handleZoomIncrease, 50);
};

const stopZoomChange = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

const handleZoomInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  // Permitir digitar livremente sem validação durante a digitação
  zoomInputValue.value = target.value;
};

const handleZoomChange = () => {
  // Validar e aplicar o zoom quando terminar de digitar (Enter ou sair do campo)
  let value = parseInt(zoomInputValue.value) || 100;
  value = Math.max(10, Math.min(200, value));
  zoomInputValue.value = value.toString();
  emit("update:zoom", value / 100);
};

const handleZoomBlur = () => {
  handleZoomChange();
};
</script>

<template>
  <header class="header">
    <div class="logo">
      <img :src="require('@/assets/images/logo-512.png')" />
    </div>
    <div class="title">RELATÓRIO</div>
    <div class="zoom">
      <button
        @click="handleZoomDecrease"
        @mousedown="startZoomDecrease"
        @mouseup="stopZoomChange"
        @mouseleave="stopZoomChange"
        @touchstart="startZoomDecrease"
        @touchend="stopZoomChange"
      >
        -
      </button>
      <input
        type="number"
        class="value"
        v-model="zoomInputValue"
        @blur="handleZoomBlur"
        @keyup.enter="handleZoomChange"
        min="10"
        max="200"
      />
      <span class="percent">%</span>
      <button
        @click="handleZoomIncrease"
        @mousedown="startZoomIncrease"
        @mouseup="stopZoomChange"
        @mouseleave="stopZoomChange"
        @touchstart="startZoomIncrease"
        @touchend="stopZoomChange"
      >
        +
      </button>
    </div>
    <div class="actions">
      <button @click="emit('download')">
        <svg-icon icon="download" size="1.25rem" />
      </button>
      <button>
        <svg-icon icon="print" size="1.4rem" />
      </button>
    </div>
    <button class="close" @click="emit('close')">
      <svg-icon icon="close" size="1rem" />
    </button>
  </header>
</template>

<style scoped lang="scss">
.header {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr auto;
  align-items: center;
  justify-content: space-between;
  padding-inline: 20px;
  height: 100%;
  background: #ffffff;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .logo {
    display: flex;
    align-items: center;
    justify-content: start;

    img {
      height: 25px;
    }
  }

  .title {
    display: flex;
    align-items: center;
    justify-content: start;
    font-size: 16px;
    color: #666;
    padding-inline: 20px;
  }

  .zoom {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    width: fit-content;
    background: #f1f1f1;
    padding: 6px;
    border-radius: 5px;
    position: relative;

    .value {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 50px;
      height: 26px;
      font-size: 15px;
      color: #666;
      background: #fff;
      padding-inline: 8px;
      border-radius: 5px;
      border: 1px solid #ccc;
      text-align: center;

      /* Remove setas do input number */
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &[type="number"] {
        -moz-appearance: textfield;
        appearance: textfield;
      }

      &:focus {
        outline: none;
        border-color: #999;
      }
    }

    .percent {
      position: absolute;
      right: 42px;
      font-size: 15px;
      color: #666;
      pointer-events: none;
    }

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      background: #ffffff;
      border: 1px solid #ccc;
      color: #b4b4b4;
      border-radius: 5px;
      padding: 0;
      cursor: pointer;
      user-select: none;

      &:hover {
        background: #f5f5f5;
        color: #666;
      }

      &:active {
        background: #e8e8e8;
      }
    }
  }

  .actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      background: transparent;
      border: 1px solid #ccc;
      color: #b4b4b4;
      border-radius: 5px;
      padding: 0;
      cursor: pointer;

      &:hover {
        background: #f5f5f5;
        color: #666;
      }
    }
  }

  .close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background: transparent;
    border: 1px solid #ccc;
    color: #b4b4b4;
    border-radius: 5px;
    padding: 0;
    margin-left: 50px;
    cursor: pointer;

    &:hover {
      background: #f5f5f5;
      color: #666;
    }
  }
}
</style>
